import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Boton } from '@/ui/Boton'
import { CampoFormulario, CampoTexto, EntradaTexto, Selector } from '@/ui/CampoFormulario'
import { CampoMonetario } from '@/ui/CampoMonetario'
import { BuscadorEntidad, type OpcionEntidad } from '@/ui/BuscadorEntidad'
import { InsigniaEstado } from '@/ui/InsigniaEstado'
import { enmascararMientrasEscribe, esRNCValido, formatearRNC, limpiarRNC } from '@/lib/rnc'
import { useClaveIdempotencia } from '@/lib/idempotencia'
import { Etiqueta, Rejilla, Seccion, Tarjeta } from './-componentes'

export const Route = createFileRoute('/sistema/formularios')({
  component: CatalogoFormularios,
})

/**
 * El mismo esquema valida en el cliente y —cuando exista OpenAPI— se comparte
 * con el servidor. Validación en dos tiempos: el esquema valida formato
 * mientras se escribe; el motor de validación previa del servidor valida las
 * reglas fiscales al emitir.
 */
const esquemaCliente = z.object({
  rnc: z
    .string()
    .min(1, 'El RNC es obligatorio.')
    .refine((valor) => esRNCValido(valor), 'Un RNC tiene 9 dígitos y una cédula 11.'),
  nombre: z.string().min(3, 'El nombre comercial tiene al menos 3 caracteres.'),
  tipo: z.enum(['cliente', 'proveedor', 'ambos']),
})

type DatosCliente = z.infer<typeof esquemaCliente>

const CONTRAPARTES: readonly OpcionEntidad[] = [
  {
    id: '1',
    titulo: 'Ferretería Sánchez',
    detalle: formatearRNC('131880738'),
    marca: <InsigniaEstado tono="verde">electrónico</InsigniaEstado>,
  },
  {
    id: '2',
    titulo: 'Transporte Rivas',
    detalle: formatearRNC('130445120'),
    marca: <InsigniaEstado tono="neutral">no electrónico</InsigniaEstado>,
  },
  {
    id: '3',
    titulo: 'Suplidora del Este',
    detalle: formatearRNC('130998812'),
    marca: <InsigniaEstado tono="verde">electrónico</InsigniaEstado>,
  },
]

function CatalogoFormularios() {
  return (
    <div className="flex flex-col gap-8">
      <Seccion
        titulo="Campo de formulario"
        nota="Etiqueta arriba, ayuda debajo, error debajo en rojo con icono — nunca solo color. Etiquetas reales, no texto de marcador: el marcador desaparece al escribir y el usuario se queda sin saber qué está llenando."
      >
        <Rejilla columnas={3}>
          <Tarjeta className="flex flex-col gap-4">
            <CampoTexto etiqueta="Nombre comercial" defaultValue="Ferretería Sánchez" />
            <CampoTexto
              etiqueta="Correo para el envío"
              ayuda="Se le envía la representación impresa al emitir."
              defaultValue="compras@ferreteriasanchez.do"
            />
            <CampoTexto
              etiqueta="RNC"
              obligatorio
              error="Un RNC tiene 9 dígitos y una cédula 11."
              defaultValue="1318807"
              numerico
            />
            <CampoTexto etiqueta="Tenant" defaultValue="Ferretería Sánchez" disabled />
          </Tarjeta>

          <Tarjeta className="flex flex-col gap-4">
            <CampoMonetarioDemo />
            <CampoRNCDemo />
            <CampoFormulario etiqueta="Tipo de e-CF" ayuda="Determina las reglas fiscales que aplican.">
              {(atributos) => (
                <Selector {...atributos} defaultValue="31">
                  <option value="31">31 · Factura de Crédito Fiscal</option>
                  <option value="32">32 · Factura de Consumo</option>
                  <option value="46">46 · Exportaciones</option>
                </Selector>
              )}
            </CampoFormulario>
          </Tarjeta>

          <Tarjeta className="flex flex-col gap-4">
            <BuscadorDemo />
            <CampoFormulario
              etiqueta="Observaciones"
              anotacion="opcional"
              ayuda="Se imprime al pie de la representación."
            >
              {(atributos) => <EntradaTexto {...atributos} placeholder="Entrega en obra" />}
            </CampoFormulario>
          </Tarjeta>
        </Rejilla>
      </Seccion>

      <Seccion
        titulo="Formulario con validación por esquema"
        nota="react-hook-form con zod. El mismo esquema que valida aquí es el que se comparte con el servidor. Envía con el formulario vacío para ver los errores debajo de cada campo."
      >
        <FormularioDeAlta />
      </Seccion>

      <Seccion
        titulo="Idempotencia"
        nota="La clave se genera una vez por instancia de formulario, nunca por render. Teclea en el campo de abajo: el formulario se vuelve a renderizar en cada pulsación y la clave no cambia."
      >
        <DemostracionIdempotencia />
      </Seccion>
    </div>
  )
}

/* ---------------------------------------------------------------------- */

function CampoMonetarioDemo() {
  const [valor, setValor] = useState<number | null>(29736)
  return (
    <CampoMonetario
      etiqueta="Total"
      valor={valor}
      onValorChange={setValor}
      ayuda="Separador de miles automático. Al entrar en el campo se edita en crudo."
      anotacion={valor === null ? 'sin valor' : null}
    />
  )
}

function CampoRNCDemo() {
  const [valor, setValor] = useState('131880738')
  const valido = esRNCValido(valor)

  return (
    <CampoFormulario
      etiqueta="RNC o cédula"
      obligatorio
      ayuda={`Se envía como ${limpiarRNC(valor) || '—'}`}
      error={valido || valor === '' ? undefined : 'Un RNC tiene 9 dígitos y una cédula 11.'}
    >
      {(atributos) => (
        <input
          {...atributos}
          inputMode="numeric"
          value={enmascararMientrasEscribe(valor)}
          onChange={(evento) => setValor(evento.target.value)}
          className="numero h-[var(--alto-control)] w-full rounded-md border border-gris-500 bg-white px-2 aria-[invalid=true]:border-rojo"
        />
      )}
    </CampoFormulario>
  )
}

function BuscadorDemo() {
  const [consulta, setConsulta] = useState('')
  const [seleccionada, setSeleccionada] = useState<OpcionEntidad | null>(null)

  const opciones = CONTRAPARTES.filter((opcion) =>
    opcion.titulo.toLowerCase().includes(consulta.toLowerCase()),
  )

  return (
    <BuscadorEntidad
      etiqueta="Cliente"
      consulta={consulta}
      onConsultaChange={setConsulta}
      opciones={consulta === '' ? CONTRAPARTES : opciones}
      seleccionada={seleccionada}
      onSeleccionar={(opcion) => {
        setSeleccionada(opcion)
        setConsulta(opcion.titulo)
      }}
      onCrear={(texto) => setConsulta(texto)}
      etiquetaCrear="Crear cliente"
      ayuda="Flechas para moverte, Enter para elegir, Esc para cerrar."
      marcador="Escribe el nombre o el RNC"
    />
  )
}

function FormularioDeAlta() {
  const [enviado, setEnviado] = useState<DatosCliente | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<DatosCliente>({
    resolver: zodResolver(esquemaCliente),
    defaultValues: { rnc: '', nombre: '', tipo: 'cliente' },
  })

  return (
    <Tarjeta>
      <form
        className="flex max-w-[560px] flex-col gap-4"
        onSubmit={handleSubmit((datos) => setEnviado(datos))}
        noValidate
      >
        <CampoTexto
          etiqueta="RNC o cédula"
          obligatorio
          numerico
          error={errors.rnc?.message}
          {...register('rnc')}
        />
        <CampoTexto
          etiqueta="Nombre comercial"
          obligatorio
          error={errors.nombre?.message}
          {...register('nombre')}
        />
        <CampoFormulario etiqueta="Tipo" error={errors.tipo?.message}>
          {(atributos) => (
            <Selector {...atributos} {...register('tipo')}>
              <option value="cliente">Cliente</option>
              <option value="proveedor">Proveedor</option>
              <option value="ambos">Ambos</option>
            </Selector>
          )}
        </CampoFormulario>

        <div className="flex items-center gap-2">
          <Boton type="submit" variante="primario">
            Guardar
          </Boton>
          {enviado ? (
            <span className="mono text-gris-600">
              enviado: {limpiarRNC(enviado.rnc)} · {enviado.nombre} · {enviado.tipo}
            </span>
          ) : null}
        </div>
      </form>
    </Tarjeta>
  )
}

function DemostracionIdempotencia() {
  const clave = useClaveIdempotencia()
  const [texto, setTexto] = useState('')

  return (
    <Tarjeta className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <Etiqueta>Idempotency-Key de esta instancia</Etiqueta>
        <span className="mono text-gris-900">{clave}</span>
      </div>
      <CampoTexto
        etiqueta="Escribe aquí para forzar renders"
        value={texto}
        onChange={(evento) => setTexto(evento.target.value)}
        ayuda={`${texto.length} renders provocados y la clave sigue siendo la misma.`}
      />
      <p className="max-w-[640px] text-etiqueta text-gris-600">
        Si el frontend manda una clave distinta en cada intento, la protección del backend no existe
        y el doble clic vuelve a duplicar. Por eso se guarda en una referencia de instancia y no en
        un <span className="mono">useMemo</span>.
      </p>
    </Tarjeta>
  )
}
